import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Tag } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWL304Query } from './hooks/usePWL304Query';
import type { DocumentRow } from '../../services/pwl304Api';
import './PWL304Form.css';

const PWL304Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWL304Query();
  const [docFrom, setDocFrom] = useState('');
  const [docTo, setDocTo] = useState('');

  const handleQuery = () => {
    runQuery({
      docFrom: docFrom.trim() || undefined,
      docTo: docTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setDocFrom('');
    setDocTo('');
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleQuery();
  };

  const columns: ColumnsType<DocumentRow> = [
    { title: '文件編號', dataIndex: 'FL040_01', key: 'FL040_01', width: 120 },
    { title: '文件名稱', dataIndex: 'FL040_11', key: 'FL040_11', ellipsis: true },
    {
      title: '發行日期',
      dataIndex: 'FL040_12',
      key: 'FL040_12',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
    {
      title: '生效日期',
      dataIndex: 'FL040_13',
      key: 'FL040_13',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
    { title: '文件類別', dataIndex: 'FL040_14', key: 'FL040_14', width: 90 },
    { title: '版次', dataIndex: 'FL040_15', key: 'FL040_15', width: 60, align: 'right' as const },
    { title: '份數', dataIndex: 'FL040_16', key: 'FL040_16', width: 60, align: 'right' as const },
    {
      title: '狀態',
      dataIndex: 'FL040_80',
      key: 'FL040_80',
      width: 80,
      render: (v: string) => v ? <Tag>{v}</Tag> : null,
    },
    {
      title: '作廢',
      dataIndex: 'FL040_79',
      key: 'FL040_79',
      width: 60,
      render: (v: boolean) => v ? <Tag color="red">作廢</Tag> : null,
    },
    { title: '建立人員', dataIndex: 'FL040_81', key: 'FL040_81', width: 90 },
    {
      title: '建立日期',
      dataIndex: 'FL040_82',
      key: 'FL040_82',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
    { title: '核准人員', dataIndex: 'FL040_83', key: 'FL040_83', width: 90 },
    {
      title: '核准日期',
      dataIndex: 'FL040_84',
      key: 'FL040_84',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
    { title: '稽核人員', dataIndex: 'FL040_93', key: 'FL040_93', width: 90 },
    { title: '附件名稱', dataIndex: 'FL050_11', key: 'FL050_11', ellipsis: true },
    { title: '附件內容', dataIndex: 'FL050_12', key: 'FL050_12', ellipsis: true },
  ];

  return (
    <PermissionGuard module={Modules.PWL304} action="query">
      <div className="pwl304-container">
        <div className="pwl304-header">
          <h2>發行文件一覽表</h2>
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>文件編號起</label>
              <Input
                value={docFrom}
                onChange={e => setDocFrom(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="起始編號"
                style={{ width: 160 }}
                allowClear
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>文件編號迄</label>
              <Input
                value={docTo}
                onChange={e => setDocTo(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="結束編號"
                style={{ width: 160 }}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  清除
                </Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<DocumentRow>
            columns={columns}
            dataSource={data}
            rowKey="FL040_01"
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1400 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWL304Form;
